import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Award, Leaf, ShieldCheck, Users, Zap, ArrowRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import GetInTouch from '../components/GetInTouch';

const stats = [
  { value: '6+', label: 'Years of Trust' },
  { value: '25+', label: 'Premium Products' },
  { value: '50K+', label: 'Happy Customers' },
  { value: '86%', label: 'Energy Savings' },
];

const values = [
  { icon: Award, title: 'Premium Quality', text: 'Every product is engineered with high-grade materials and rigorous quality control.' },
  { icon: Leaf, title: 'Energy Efficient', text: 'Save up to 86% energy compared to traditional lighting, lowering your bills.' },
  { icon: ShieldCheck, title: 'Reliable & Safe', text: 'Flicker-free, eye-comfort technology with surge and short-circuit protection.' },
  { icon: Users, title: 'Customer First', text: 'Nationwide delivery and dedicated after-sales support with a 14-month warranty.' },
];

const AboutUs = () => (
  <div>
    <PageHero title="About National Light" subtitle="We Bright The Nation — delivering smart, energy-efficient LED lighting solutions across Pakistan." />

    <section className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 items-center">
      <div>
        <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">Who We Are</span>
        <h2 className="font-head text-3xl font-extrabold text-[#14181b] mt-2">Smart Technology for a Brighter Future</h2>
        <p className="text-gray-600 mt-4 leading-relaxed">National Light is a leading LED lighting brand committed to illuminating homes, offices, commercial and industrial spaces with premium quality and unmatched efficiency. For over 6 years, we have engineered lighting solutions that combine advanced technology with exceptional durability.</p>
        <p className="text-gray-600 mt-4 leading-relaxed">From LED bulbs and panel lights to powerful flood lights, our products are designed to deliver brilliant illumination while reducing energy consumption and operating costs. Every National Light product carries our promise of quality, reliability, and a 14-month warranty.</p>
        <Link to="/shop" className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-7 py-3 mt-6">Explore Products <ArrowRight size={18} /></Link>
      </div>
      <div className="relative">
        <img src="/about/collage.webp" alt="National Light team, manufacturing and products"
          className="rounded-3xl w-full object-cover shadow-xl border border-gray-100" />
        <div className="absolute -bottom-5 -left-3 bg-white rounded-2xl shadow-lg border border-gray-100 px-5 py-3 hidden sm:flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#0f7a37] flex items-center justify-center"><Award size={20} className="text-white" /></div>
          <div>
            <div className="font-head font-extrabold text-[#0d5228] leading-none text-lg">6+ Years</div>
            <div className="text-xs text-gray-500">of Trusted Quality</div>
          </div>
        </div>
      </div>
    </section>

    {/* Stats band */}
    <section className="section-soft">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {stats.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-7 text-center card-hover">
              <div className="font-head text-4xl font-extrabold text-[#0d5228]">{s.value}</div>
              <div className="text-sm text-gray-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-soft pt-0">
      <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <div className="w-12 h-12 rounded-xl bg-[#eaf6ec] flex items-center justify-center mb-4"><Target size={24} className="text-[#0f7a37]" /></div>
          <h3 className="font-head text-xl font-bold text-[#14181b]">Our Mission</h3>
          <p className="text-gray-600 mt-3 leading-relaxed">To brighten every space across the nation with energy-efficient, affordable and long-lasting LED lighting, empowering families and businesses to save energy and money.</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <div className="w-12 h-12 rounded-xl bg-[#eaf6ec] flex items-center justify-center mb-4"><Eye size={24} className="text-[#0f7a37]" /></div>
          <h3 className="font-head text-xl font-bold text-[#14181b]">Our Vision</h3>
          <p className="text-gray-600 mt-3 leading-relaxed">To be Pakistan's most trusted lighting brand, driving a sustainable future through innovation, quality and a relentless commitment to customer satisfaction.</p>
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="text-center mb-10">
        <span className="text-[#37b34a] font-semibold uppercase tracking-wider text-sm">Why Choose Us</span>
        <h2 className="font-head text-3xl font-extrabold text-[#14181b] mt-2">Our Core Values</h2>
        <div className="w-20 h-1 bg-[#37b34a] mx-auto mt-4 rounded-full" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {values.map((v, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 card-hover">
            <div className="w-12 h-12 rounded-full bg-[#0f7a37] flex items-center justify-center mb-4"><v.icon size={22} className="text-white" /></div>
            <h3 className="font-head font-bold text-lg text-[#14181b]">{v.title}</h3>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{v.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="section-soft">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-8">
          <h2 className="font-head text-3xl font-extrabold text-[#14181b]">Get In Touch</h2>
          <p className="text-gray-500 mt-2">Have a question? Send us a message and our team will respond promptly.</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-8"><GetInTouch /></div>
      </div>
    </section>
  </div>
);

export default AboutUs;
